import { useState } from 'react';
import api from '../../lib/api';
import { Button } from './button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { Download, FileText, FileSpreadsheet, Printer } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Reusable export button + format-chooser dialog.
 * Usage:
 *   <ExportToolbar endpoint="/exports/sales-orders" params={{ start_date, end_date }} fileBaseName="sales-orders" />
 *
 * Modes:
 *   - "register" (default): asks PDF or Excel → calls GET endpoint?format=<chosen>
 *   - "single"  : downloads single PDF (no chooser) → calls GET endpoint
 */
export function ExportToolbar({
  endpoint,
  params = {},
  fileBaseName = 'export',
  mode = 'register',
  label,
  size = 'sm',
  variant = 'outline',
  iconOnly = false,
  testId = 'export-toolbar',
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const doDownload = async (format) => {
    setBusy(true);
    try {
      const res = await api.get(endpoint, {
        params: format ? { ...params, format } : params,
        responseType: 'blob',
      });
      const mime = (!format || format === 'pdf')
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const blob = new Blob([res.data], { type: mime });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${fileBaseName}.${format || 'pdf'}`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 1500);
      if (format) toast.success(`${format.toUpperCase()} downloaded`);
      setOpen(false);
    } catch (err) {
      toast.error('Failed to generate export');
    } finally { setBusy(false); }
  };

  if (mode === 'single') {
    return (
      <Button
        type="button"
        variant={variant}
        size={size}
        onClick={() => doDownload(null)}
        disabled={busy || disabled}
        data-testid={testId}
        title="Print PDF"
      >
        <Printer className={iconOnly ? 'h-4 w-4' : 'h-4 w-4 mr-1'} />
        {!iconOnly && (label || 'Print')}
      </Button>
    );
  }

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        onClick={() => setOpen(true)}
        disabled={disabled}
        data-testid={testId}
      >
        <Download className={iconOnly ? 'h-4 w-4' : 'h-4 w-4 mr-1'} />
        {!iconOnly && (label || 'Print / Export')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export {label || 'Register'}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Choose a format to download.</p>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <button
              type="button"
              onClick={() => doDownload('pdf')}
              disabled={busy}
              className="border rounded-lg p-4 hover:border-primary hover:bg-muted/40 transition-colors flex flex-col items-center gap-2 disabled:opacity-50"
              data-testid={`${testId}-pdf`}
            >
              <FileText className="h-8 w-8 text-red-500" />
              <span className="font-medium">PDF</span>
              <span className="text-xs text-muted-foreground">For printing / sharing</span>
            </button>
            <button
              type="button"
              onClick={() => doDownload('xlsx')}
              disabled={busy}
              className="border rounded-lg p-4 hover:border-primary hover:bg-muted/40 transition-colors flex flex-col items-center gap-2 disabled:opacity-50"
              data-testid={`${testId}-xlsx`}
            >
              <FileSpreadsheet className="h-8 w-8 text-green-600" />
              <span className="font-medium">Excel</span>
              <span className="text-xs text-muted-foreground">Editable spreadsheet</span>
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
